import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { BadgeCheck, Clock, ShieldAlert, Search as SearchIcon, Users } from 'lucide-react';
import Header from '../components/Header';
import ProUpgradeCard from '../components/ProUpgradeCard';
import { useAuth } from '../context/AuthContext';
import { api, formatError } from '../lib/api';
import { toast } from 'sonner';

const CLIENT_TAGS = ['bad_payer', 'unrealistic_expectations', 'aggressive', 'no_show', 'changed_scope', 'safety_issue'];

export default function TradieDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState('profile');
  const [categories, setCategories] = useState([]);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    business_name: '', categories: [], suburb: '', service_areas: '', bio: '',
    license_number: '', hourly_rate: '', avatar_url: '', portfolio: '', lat: '', lng: ''
  });
  const [saving, setSaving] = useState(false);

  // Client review state
  const [crQuery, setCrQuery] = useState('');
  const [crResults, setCrResults] = useState([]);
  const [crFlags, setCrFlags] = useState([]);
  const [addrCheck, setAddrCheck] = useState('');
  const [addrHits, setAddrHits] = useState([]);
  const [crForm, setCrForm] = useState({ client_name: '', client_suburb: '', client_address: '', rating: 3, comment: '', tags: [] });
  const [crBusy, setCrBusy] = useState(false);

  useEffect(() => {
    api.categories().then((r) => setCategories(r.data));
    // Heartbeat for the "Live now" green dot — ping now + every 3 minutes.
    const beat = () => api.tradieHeartbeat().catch(() => {});
    beat();
    const hb = setInterval(beat, 3 * 60 * 1000);
    api.myTradieProfile().then((r) => {
      if (r.data) {
        setProfile(r.data);
        setForm({
          business_name: r.data.business_name || '',
          categories: r.data.categories || [],
          suburb: r.data.suburb || '',
          service_areas: (r.data.service_areas || []).join(', '),
          bio: r.data.bio || '',
          license_number: r.data.license_number || '',
          hourly_rate: r.data.hourly_rate || '',
          avatar_url: r.data.avatar_url || '',
          portfolio: (r.data.portfolio || []).join('\n'),
          lat: r.data.lat || '',
          lng: r.data.lng || '',
        });
      }
    });
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => {}, []);

  const loadClientReviews = async () => {
    try {
      const [{ data }, flags] = await Promise.all([
        api.searchClientReviews(crQuery || undefined),
        api.clientFlags(),
      ]);
      setCrResults(data);
      setCrFlags(flags.data);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  useEffect(() => { if (tab === 'clients') loadClientReviews(); /* eslint-disable-line */ }, [tab]);

  // Live check when address input changes (debounced)
  useEffect(() => {
    if (!addrCheck.trim() || addrCheck.trim().length < 3) { setAddrHits([]); return; }
    const h = setTimeout(async () => {
      try {
        const { data } = await api.searchClientReviews(addrCheck.trim());
        setAddrHits(data);
      } catch { /* ignore */ }
    }, 350);
    return () => clearTimeout(h);
  }, [addrCheck]);

  if (user && user.role !== 'tradie') return <Navigate to="/" replace />;

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        business_name: form.business_name.trim(),
        categories: form.categories,
        suburb: form.suburb.trim(),
        service_areas: form.service_areas.split(',').map((s) => s.trim()).filter(Boolean),
        bio: form.bio,
        license_number: form.license_number || null,
        hourly_rate: form.hourly_rate ? parseFloat(form.hourly_rate) : null,
        avatar_url: form.avatar_url || null,
        portfolio: form.portfolio.split('\n').map((s) => s.trim()).filter(Boolean),
        lat: form.lat ? parseFloat(form.lat) : null,
        lng: form.lng ? parseFloat(form.lng) : null,
      };
      const { data } = await api.upsertTradieProfile(payload);
      setProfile(data);
      toast.success(data.verified ? 'Profile updated' : 'Saved. Awaiting admin verification.');
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (id) => {
    setForm((f) => ({
      ...f,
      categories: f.categories.includes(id) ? f.categories.filter((x) => x !== id) : [...f.categories, id]
    }));
  };

  const toggleCrTag = (t) => {
    setCrForm((f) => ({ ...f, tags: f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t] }));
  };

  const submitClientReview = async (e) => {
    e.preventDefault();
    if (!crForm.client_name.trim() || !crForm.client_suburb.trim() || !crForm.comment.trim()) return;
    setCrBusy(true);
    try {
      await api.postClientReview({
        client_name: crForm.client_name.trim(),
        client_suburb: crForm.client_suburb.trim(),
        client_address: crForm.client_address.trim() || null,
        rating: parseInt(crForm.rating, 10),
        comment: crForm.comment.trim(),
        tags: crForm.tags,
      });
      setCrForm({ client_name: '', client_suburb: '', client_address: '', rating: 3, comment: '', tags: [] });
      toast.success('Recorded. Only verified tradies will see this.');
      loadClientReviews();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setCrBusy(false);
    }
  };

  const toggleAvailable = async () => {
    if (!profile) return;
    try {
      const { data } = await api.setAvailable(!profile.available_today);
      setProfile({ ...profile, available_today: data.available_today });
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="tradie-dashboard">
      <Header />

      <div className="mx-auto max-w-6xl px-6 py-10 md:px-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#8A8988]">Tradie dashboard</div>
            <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight text-[#1C1B1A]">{profile?.business_name || 'Set up your trade'}</h1>
            {profile && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                {profile.verified ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#2A9D8F]/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#2A9D8F]" data-testid="dashboard-verified-badge">
                    <BadgeCheck size={12} /> Verified
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#E9C46A]/20 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#B08911]" data-testid="dashboard-pending-badge">
                    <Clock size={12} /> Awaiting verification
                  </span>
                )}
                {profile.verified && (
                  <button onClick={toggleAvailable} data-testid="available-toggle"
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] ${profile.available_today ? 'bg-[#D96C4E] text-white' : 'border border-[#E5E4E0] text-[#5C5B5A]'}`}>
                    {profile.available_today ? 'Available today ✓' : 'Set available today'}
                  </button>
                )}
              </div>
            )}
          </div>
          {profile?.verified && (
            <Link to={`/tradie/${profile.id}`} className="btn-ghost" data-testid="view-public-profile">View public profile →</Link>
          )}
        </div>

        {/* Tabs */}
        <div className="mt-8 border-b border-[#E5E4E0]" data-testid="dashboard-tabs">
          {[
            { id: 'profile', label: 'My profile' },
            { id: 'clients', label: 'Client notes (tradies only)', icon: <ShieldAlert size={14} /> },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`inline-flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium ${tab === t.id ? 'border-[#1A362D] text-[#1A362D]' : 'border-transparent text-[#5C5B5A]'}`}
            >
              {t.icon}{t.label}
            </button>
          ))}
        </div>

        {tab === 'profile' && <ProUpgradeCard />}

        {tab === 'profile' && (
          <form onSubmit={saveProfile} className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-2" data-testid="profile-form">
            <div className="space-y-4">
              <div>
                <label className="label-editorial">Business name</label>
                <input required value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="input-business-name" className="input-editorial" />
              </div>
              <div>
                <label className="label-editorial">Suburb</label>
                <input required value={form.suburb} onChange={(e) => setForm({ ...form, suburb: e.target.value })} data-testid="input-suburb" className="input-editorial" placeholder="e.g. Fremantle" />
              </div>
              <div>
                <label className="label-editorial">Service areas (comma-separated)</label>
                <input value={form.service_areas} onChange={(e) => setForm({ ...form, service_areas: e.target.value })} data-testid="input-service-areas" className="input-editorial" placeholder="Fremantle, East Fremantle, North Fremantle" />
              </div>
              <div>
                <label className="label-editorial">Bio</label>
                <textarea rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="input-bio" className="input-editorial" placeholder="Who you are, what you're good at." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-editorial">License #</label>
                  <input value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} data-testid="input-license" className="input-editorial" />
                </div>
                <div>
                  <label className="label-editorial">Hourly rate ($)</label>
                  <input type="number" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} data-testid="input-rate" className="input-editorial" />
                </div>
              </div>
              <div>
                <label className="label-editorial">Avatar image URL</label>
                <input value={form.avatar_url} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })} data-testid="input-avatar" className="input-editorial" placeholder="https://..." />
              </div>
              <div>
                <label className="label-editorial">Portfolio image URLs (one per line)</label>
                <textarea rows={3} value={form.portfolio} onChange={(e) => setForm({ ...form, portfolio: e.target.value })} data-testid="input-portfolio" className="input-editorial" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-editorial">Latitude</label>
                  <input value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} data-testid="input-lat" className="input-editorial" placeholder="-31.95" />
                </div>
                <div>
                  <label className="label-editorial">Longitude</label>
                  <input value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} data-testid="input-lng" className="input-editorial" placeholder="115.86" />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="label-editorial">Trades you offer</label>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="category-picker">
                  {categories.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleCategory(c.id)}
                      data-testid={`cat-${c.id}`}
                      className={`rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] ${form.categories.includes(c.id) ? 'border-[#1A362D] bg-[#1A362D] text-white' : 'border-[#E5E4E0] bg-white text-[#5C5B5A]'}`}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="border border-[#E5E4E0] bg-white p-4">
                <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#8A8988]">Verification checklist</div>
                <ul className="mt-2 space-y-1 text-sm text-[#5C5B5A]">
                  <li>• Business name & suburb</li>
                  <li>• At least one trade category</li>
                  <li>• License number (if required for your trade)</li>
                  <li>• Avatar or portfolio photo</li>
                </ul>
                <p className="mt-3 text-xs text-[#8A8988]">An admin reviews new profiles within 24 hours before you go live.</p>
              </div>

              <button type="submit" disabled={saving} data-testid="save-profile-btn" className="btn-primary w-full justify-center disabled:opacity-50">
                {saving ? 'Saving...' : profile ? 'Save changes' : 'Submit profile for review'}
              </button>
            </div>
          </form>
        )}

        {tab === 'clients' && (
          <div className="mt-8">
            {!profile?.verified ? (
              <div className="border border-[#E9C46A] bg-[#E9C46A]/10 p-6" data-testid="clients-locked">
                <div className="flex items-center gap-2 text-sm font-semibold text-[#B08911]">
                  <ShieldAlert size={16} /> This area unlocks once your profile is verified.
                </div>
                <p className="mt-2 text-sm text-[#5C5B5A]">
                  Verified tradies can post and browse private client experiences — bad payers, no-shows, dodgy job sites. This never appears on public profiles.
                </p>
              </div>
            ) : (
              <div className="space-y-8">
                {/* Killer "Check job address" quick-check bar */}
                <div className="border-2 border-[#1A362D] bg-white p-6" data-testid="address-check-panel">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#1A362D]">
                    <ShieldAlert size={14} /> Before you drive out — check the address
                  </div>
                  <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
                    <input
                      value={addrCheck}
                      onChange={(e) => setAddrCheck(e.target.value)}
                      placeholder="Type client name, suburb or street address..."
                      data-testid="address-check-input"
                      className="input-editorial flex-1"
                    />
                  </div>
                  {addrCheck.trim().length >= 3 && (
                    <div className="mt-4" data-testid="address-check-results">
                      {addrHits.length === 0 ? (
                        <div className="flex items-center gap-2 text-sm text-[#2A9D8F]">
                          <ShieldAlert size={16} /> No warnings on file for this location. Take the job.
                        </div>
                      ) : (
                        <div className="border-l-4 border-[#E76F51] bg-[#E76F51]/5 p-4">
                          <div className="text-sm font-bold uppercase tracking-[0.15em] text-[#E76F51]">
                            {addrHits.length} warning{addrHits.length > 1 ? 's' : ''} on file for this address / name
                          </div>
                          <ul className="mt-3 space-y-2 text-sm text-[#1C1B1A]">
                            {addrHits.slice(0, 5).map((h) => (
                              <li key={h.id} className="border-l-2 border-[#E76F51]/40 pl-3">
                                <div className="text-xs uppercase tracking-[0.15em] text-[#8A8988]">{h.author_business_name} · {'★'.repeat(h.rating)}</div>
                                <div className="mt-0.5"><b>{h.client_name}</b> · {h.client_suburb}{h.client_address && ` · ${h.client_address}`}</div>
                                <div className="mt-1 text-[#5C5B5A]">{h.comment}</div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr,1.2fr]">
                {/* Post form */}
                <form onSubmit={submitClientReview} className="border border-[#1C1B1A] bg-[#F3F2EE] p-6" data-testid="client-review-form">                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#1A362D]">
                    <Users size={14} /> Tradies-only • not public
                  </div>
                  <h3 className="mt-2 font-heading text-2xl font-bold text-[#1C1B1A]">Log an experience</h3>
                  <div className="mt-4 space-y-3">
                    <input required value={crForm.client_name} onChange={(e) => setCrForm({ ...crForm, client_name: e.target.value })} placeholder="Client name" data-testid="cr-client-name" className="input-editorial" />
                    <input required value={crForm.client_suburb} onChange={(e) => setCrForm({ ...crForm, client_suburb: e.target.value })} placeholder="Suburb" data-testid="cr-client-suburb" className="input-editorial" />
                    <input value={crForm.client_address} onChange={(e) => setCrForm({ ...crForm, client_address: e.target.value })} placeholder="Address (optional)" data-testid="cr-client-address" className="input-editorial" />
                    <div>
                      <label className="label-editorial">Rating</label>
                      <select value={crForm.rating} onChange={(e) => setCrForm({ ...crForm, rating: e.target.value })} data-testid="cr-rating" className="input-editorial">
                        <option value={1}>★ 1 - Avoid</option>
                        <option value={2}>★★ 2 - Difficult</option>
                        <option value={3}>★★★ 3 - Mixed</option>
                        <option value={4}>★★★★ 4 - Fine</option>
                        <option value={5}>★★★★★ 5 - Dream client</option>
                      </select>
                    </div>
                    <textarea required rows={3} value={crForm.comment} onChange={(e) => setCrForm({ ...crForm, comment: e.target.value })}
                      placeholder="What happened? Be factual and fair." data-testid="cr-comment" className="input-editorial" />
                    <div>
                      <label className="label-editorial">Tags</label>
                      <div className="mt-1 flex flex-wrap gap-1.5" data-testid="cr-tags">
                        {CLIENT_TAGS.map((t) => (
                          <button
                            type="button"
                            key={t}
                            onClick={() => toggleCrTag(t)}
                            data-testid={`cr-tag-${t}`}
                            className={`rounded-full border px-2.5 py-1 text-xs ${crForm.tags.includes(t) ? 'border-[#1A362D] bg-[#1A362D] text-white' : 'border-[#E5E4E0] bg-white text-[#5C5B5A]'}`}
                          >
                            {t.replace(/_/g, ' ')}
                          </button>
                        ))}
                      </div>
                    </div>
                    <button type="submit" disabled={crBusy} data-testid="cr-submit" className="btn-primary w-full justify-center disabled:opacity-50">
                      {crBusy ? 'Posting...' : 'Log experience'}
                    </button>
                  </div>
                </form>

                {/* Search results */}
                <div>
                  {/* Flagged clients banner — appears whenever any client has 2+ warnings */}
                  {crFlags.length > 0 && (
                    <div className="mb-4 border-l-4 border-[#E76F51] bg-[#E76F51]/5 p-4" data-testid="flagged-clients-banner">
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#E76F51]">
                        <ShieldAlert size={14} /> Flagged clients — heard about by multiple tradies
                      </div>
                      <div className="mt-3 space-y-2">
                        {crFlags.map((f) => (
                          <div key={f.client_name + f.client_suburb} className="flex flex-wrap items-center gap-3" data-testid={`flag-${f.client_name}`}>
                            <span className="text-sm font-semibold text-[#1C1B1A]">{f.client_name}</span>
                            <span className="text-xs text-[#5C5B5A]">· {f.client_suburb}</span>
                            <span className="rounded-full bg-[#E76F51] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-white">
                              Warned by {f.warning_count} tradies
                            </span>
                            {f.common_tags.map((t) => (
                              <span key={t} className="text-[10px] uppercase tracking-[0.1em] text-[#E76F51]">#{t.replace(/_/g, ' ')}</span>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2 border border-[#E5E4E0] bg-white px-3 py-2">
                    <SearchIcon size={16} className="text-[#8A8988]" />
                    <input
                      value={crQuery}
                      onChange={(e) => setCrQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && loadClientReviews()}
                      placeholder="Search by name, suburb, or address..."
                      data-testid="cr-search-input"
                      className="w-full bg-transparent text-sm outline-none"
                    />
                    <button onClick={loadClientReviews} data-testid="cr-search-btn" className="text-xs font-semibold text-[#1A362D]">Search</button>
                  </div>

                  <div className="mt-4 space-y-3" data-testid="cr-results">
                    {crResults.length === 0 && <div className="border border-[#E5E4E0] bg-white p-4 text-sm text-[#8A8988]">No entries yet.</div>}
                    {crResults.map((r) => (
                      <div key={r.id} className="border border-[#E5E4E0] bg-white p-4" data-testid={`cr-item-${r.id}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-semibold text-[#1C1B1A]">{r.client_name}</div>
                            <div className="text-xs text-[#8A8988]">{r.client_suburb}{r.client_address && ` · ${r.client_address}`}</div>
                          </div>
                          <div className="text-sm font-semibold text-[#E76F51]">{'★'.repeat(r.rating)}</div>
                        </div>
                        <p className="mt-2 text-sm text-[#5C5B5A]">{r.comment}</p>
                        {r.tags?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {r.tags.map((t) => (
                              <span key={t} className="rounded-full bg-[#E76F51]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-[#E76F51]">{t.replace(/_/g, ' ')}</span>
                            ))}
                          </div>
                        )}
                        <div className="mt-2 text-[10px] uppercase tracking-[0.15em] text-[#8A8988]">by {r.author_business_name}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
